import React, { useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import AuthService from '../../services/authService';
import { apiErrToMessage } from '../../utils/apiError';
import { dispatchQuotaUpdated } from '../../utils/quotaEvent';
import ProtectedMenuPage from '../../components/ProtectedMenuPage/ProtectedMenuPage';
import KakaoFloatButton from '../../components/KakaoFloatButton/KakaoFloatButton';
import './ChangeUrl.css';
import { Card, Row, Col, Button, Space, Input, Tag, message } from 'antd';
import { CopyOutlined, DownloadOutlined, ReloadOutlined, SwapOutlined } from '@ant-design/icons';

const { TextArea } = Input;

const ChangeUrl = ({ title: _title = '상품 주소 변환' }) => {
    const [inputText, setInputText] = useState('');
    const [converted, setConverted] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hasConverted, setHasConverted] = useState(false);
    const _wrapperRef = useRef(null);

    const handleConvert = async () => {
        if (!inputText.trim()) return message.warning('주소를 입력해주세요.');
        setLoading(true);
        setHasConverted(true);

        try {
            const cleaned = inputText.replace(/^❌\s*/gm, '').trim();
            const lines = cleaned
                .split(/\r?\n/)
                .map(l => l.trim())
                .filter(Boolean);

            const valid = [];
            const processed = [];
            const invalid = [];

            const urlPattern = /^https?:\/\/[^\s'"]+$/i;

            for (const line of lines) {
                const original = line;

                if (!/^https?:\/\//i.test(line)) {
                    processed.push(`❌ ${original}`);
                    invalid.push(original);
                    continue;
                }

                if (/\s/.test(line)) {
                    processed.push(`❌ ${original}`);
                    invalid.push(original);
                    continue;
                }

                if (!urlPattern.test(line)) {
                    processed.push(`❌ ${original}`);
                    invalid.push(original);
                    continue;
                }

                let url = line;

                try {
                    const qIndex = line.indexOf('?');
                    if (qIndex > 0) {
                        const beforeQ = line.substring(0, qIndex);
                        const lastPath = beforeQ.split('/').pop();
                        const u = new URL(line);
                        const base = `${u.origin}${u.pathname}`;

                        // ✅ 1. ? 바로 앞이 숫자만 → 쿼리 전체 제거
                        if (/^\d+$/.test(lastPath)) {
                            url = beforeQ;
                        }
                        // ✅ 2. 특정 상품 번호 파라미터 유지
                        else if (
                            u.searchParams.has('product_no') ||
                            u.searchParams.has('no') ||
                            u.searchParams.has('goodsno') ||
                            u.searchParams.has('item_id')
                        ) {
                            const key = ['product_no', 'no', 'goodsno', 'item_id'].find(k =>
                                u.searchParams.has(k)
                            );
                            const val = u.searchParams.get(key);
                            url = `${base}?${key}=${val}`;
                        }
                        // ✅ 3. 그 외 → 첫 번째 파라미터만 유지
                        else {
                            const firstKey = u.searchParams.keys().next().value;
                            if (firstKey) {
                                const firstVal = u.searchParams.get(firstKey);
                                url = `${base}?${firstKey}=${firstVal}`;
                            } else {
                                url = base;
                            }
                        }
                    } else {
                        url = line;
                    }
                } catch {
                    processed.push(`❌ ${original}`);
                    invalid.push(original);
                    continue;
                }

                processed.push(original);
                valid.push(url);
            }

            setConverted(valid);
            setInputText(processed.join('\n'));

            if (invalid.length > 0)
                message.warning(
                    `⚠️ ${invalid.length}개의 줄에 오류가 있습니다.\n좌측의 ❌ 표시 줄을 확인해주세요.`
                );

            try {
                await AuthService.authAxios.post('/tools/change-url/history', {
                    total: lines.length,
                    success: valid.length,
                    fail: invalid.length,
                    timestamp: new Date().toISOString(),
                });
                dispatchQuotaUpdated('changeUrl');
            } catch (err) {
                const errMsg = apiErrToMessage(err.response?.data, err.response?.status);
                message.error(errMsg);
                dispatchQuotaUpdated('changeUrl');
            }
        } catch (err) {
            console.error(err);
            message.error('변환 중 오류가 발생했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const handleCopy = () => {
        if (converted.length === 0) return message.warning('복사할 내용이 없습니다.');
        navigator.clipboard.writeText(converted.join('\n'));
        message.success('복사되었습니다!');
    };

    const handleExcelDownload = () => {
        if (converted.length === 0) return message.warning('엑셀로 저장할 변환 결과가 없습니다.');
        const exportData = converted.map(u => ({ URL: u }));
        const worksheet = XLSX.utils.json_to_sheet(exportData, { header: ['URL'] });
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, '주소변환결과');
        const now = new Date();
        const timestamp = now.toISOString().slice(0, 19).replace(/[-T:]/g, '');
        const filename = `주소변환결과_${timestamp}.xlsx`;
        XLSX.writeFile(workbook, filename);
    };

    return (
        <ProtectedMenuPage menuKey="changeUrl">
            <div className="change-url-page">
                <div className="page-content-inner">
                    <Row gutter={24} align="stretch">
                        <Col xl={10} lg={12} md={24} className="change-url-col-left">
                            <Card
                                title={
                                    <Row
                                        justify="space-between"
                                        align="middle"
                                        style={{ width: '100%' }}
                                    >
                                        <Col>주소 입력</Col>
                                        <Col>
                                            <Space size="small">
                                                <Button
                                                    icon={<ReloadOutlined />}
                                                    onClick={() => {
                                                        setInputText('');
                                                        setConverted([]);
                                                        setHasConverted(false);
                                                    }}
                                                    className="page-action-btn"
                                                    size="small"
                                                >
                                                    초기화
                                                </Button>
                                                <Button
                                                    type="primary"
                                                    icon={<SwapOutlined />}
                                                    onClick={handleConvert}
                                                    loading={loading}
                                                    className="search-btn-premium"
                                                    size="small"
                                                    style={{ minWidth: '110px' }}
                                                >
                                                    주소 변환
                                                </Button>
                                            </Space>
                                        </Col>
                                    </Row>
                                }
                                bordered={false}
                                className="control-card change-url-input-card"
                            >
                                <TextArea
                                    placeholder="여러 개의 주소를 한 줄씩 입력하거나 여기에 붙여넣기 (Ctrl+V)"
                                    value={inputText}
                                    onChange={e => setInputText(e.target.value)}
                                    className="change-url-textarea"
                                    disabled={loading}
                                />
                            </Card>
                        </Col>

                        <Col xl={14} lg={12} md={24} className="change-url-col-right">
                            <Card
                                title={
                                    <Row
                                        justify="space-between"
                                        align="middle"
                                        style={{ width: '100%' }}
                                    >
                                        <Col>변환 결과</Col>
                                        <Col>
                                            <Space size="small">
                                                {(() => {
                                                    const total = inputText
                                                        ? inputText
                                                              .split(/\r?\n/)
                                                              .filter(l => l.trim()).length
                                                        : 0;
                                                    const success = converted.length;
                                                    const fail = total - success;
                                                    const hasError = hasConverted && fail > 0;
                                                    const showNeutral = !hasConverted;
                                                    const tagClass = showNeutral
                                                        ? 'change-url-count-tag tag-neutral'
                                                        : hasError
                                                          ? 'change-url-count-tag tag-error'
                                                          : 'change-url-count-tag tag-success';
                                                    return (
                                                        <Tag className={tagClass}>
                                                            요청: {total} / 성공: {success} / 실패:{' '}
                                                            {hasConverted ? fail : 0}
                                                        </Tag>
                                                    );
                                                })()}
                                                <Button
                                                    icon={<CopyOutlined />}
                                                    onClick={handleCopy}
                                                    disabled={converted.length === 0}
                                                    size="small"
                                                    className="page-action-btn"
                                                >
                                                    복사
                                                </Button>
                                                <Button
                                                    className="excel-button"
                                                    icon={<DownloadOutlined />}
                                                    onClick={handleExcelDownload}
                                                    disabled={converted.length === 0}
                                                    title="엑셀 다운로드"
                                                    size="small"
                                                />
                                            </Space>
                                        </Col>
                                    </Row>
                                }
                                bordered={false}
                                className="change-url-result-card"
                            >
                                <TextArea
                                    readOnly
                                    value={converted.join('\n')}
                                    className="change-url-textarea change-url-textarea-result"
                                    placeholder="변환 결과가 여기에 표시됩니다."
                                />
                            </Card>
                        </Col>
                    </Row>
                </div>
            </div>
            <KakaoFloatButton />
        </ProtectedMenuPage>
    );
};

export { ChangeUrl };
